import type { MouseEvent, PropsWithChildren } from "react";
import { css, styled } from "styled-components";

export type DropdownItemProps = PropsWithChildren & {
  index?: number;
  isActive?: boolean;
  isDisabled?: boolean;
  isProItem?: boolean;
  onClick?: (event: MouseEvent) => void;
};

const getTransition = (index: number) => `
  opacity 0.3s ${0.15 + 0.05 * index}s ease,
  transform 0.3s ${0.15 + 0.05 * index}s ease
`;

const StyledProContainer = styled.div<{ index: number; isDisabled?: boolean }>`
  white-space: nowrap;
  position: relative;
  transition:
    ${({ index }) => getTransition(index)},
    background-color 0.3s;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.proLabelBackground};
  color: ${({ theme }) => theme.colors.proLabelText}AA;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.proLabelBackgroundActive};
    color: ${({ theme }) => theme.colors.proLabelText};
  }
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

const StyledContainer = styled.div<{ index: number; isActive: boolean; isDisabled?: boolean }>`
  white-space: nowrap;
  position: relative;
  transition:
    ${({ index }) => getTransition(index)},
    background-color 0.3s;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.dropdownBackground};
  color: ${({ theme }) => theme.colors.textFade};

  ${({ theme, isDisabled }) =>
    isDisabled
      ? css`
          cursor: not-allowed;
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: ${theme.colors.dropdownActiveBackground};
            color: ${theme.colors.textBright};
          }
        `}

  ${({ isActive }) => css`
    opacity: ${isActive ? 1 : 0};
    transform: translate3d(${isActive ? 0 : -20}px, 0, 0);
  `};
`;

export const DropdownItem = ({
  children,
  index = 1,
  isActive = false,
  isDisabled,
  isProItem,
  onClick,
}: DropdownItemProps) =>
  isProItem ? (
    <StyledProContainer index={index + 1} onClick={onClick} isDisabled={isDisabled}>
      {children}
    </StyledProContainer>
  ) : (
    <StyledContainer index={index + 1} isActive={isActive} isDisabled={isDisabled} onClick={onClick}>
      {children}
    </StyledContainer>
  );
